import React, { useEffect, useState } from "react";
import { LuTriangleAlert } from "react-icons/lu";
import { Badge, Button, ConfirmDialog, SkeletonRows } from "../../../components/ui";
import { useEmailAccount } from "../../../hooks/useEmailAccount";
import { t } from "../../../i18n";
import { ITEM_CLASS } from "./LinkedList";

// Conta Google usada para enviar os PDFs por e-mail: conectar, ver qual está conectada, desconectar.
export const EmailAccountSection: React.FC = () => {
  const { status, fetchStatus, statusFailed, connectGoogle, connecting, disconnectGoogle, disconnecting } = useEmailAccount();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDisconnect = async () => {
    setConfirmingDisconnect(false);
    if (await disconnectGoogle()) fetchStatus();
  };

  // Sem status e sem erro = ainda verificando (inclui o primeiro render, antes do fetch).
  if (!status && !statusFailed) {
    return <SkeletonRows label={t("settings.emailConnection.checking")} rows={1} />;
  }

  // Falhou a verificação: não dá para dizer se está conectada, então não oferece "Conectar".
  if (!status) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-body text-fg-muted">{t("settings.emailConnection.loadError")}</p>
        <Button variant="outline" size="sm" onClick={fetchStatus}>{t("common.retry")}</Button>
      </div>
    );
  }

  if (status?.connected) {
    return (
      <>
        {confirmingDisconnect && status.email && (
          <ConfirmDialog
            title={t("settings.emailConnection.disconnectTitle")}
            message={t("settings.emailConnection.disconnectConfirm", { email: status.email })}
            confirmLabel={t("settings.emailConnection.disconnect")}
            onConfirm={handleDisconnect}
            onCancel={() => setConfirmingDisconnect(false)}
          />
        )}
        <div className={`${ITEM_CLASS} flex-wrap`}>
          <div className="min-w-0 flex-1">
            <span className="block break-words text-body font-semibold text-fg">{status.email}</span>
            <span className="block text-caption text-fg-subtle">{t("settings.emailConnection.connected")}</span>
          </div>
          <Badge tone="success">{t("settings.emailConnection.connectedBadge")}</Badge>
          <Button variant="danger-ghost" size="sm" onClick={() => setConfirmingDisconnect(true)} disabled={disconnecting}>
            {t("settings.emailConnection.disconnect")}
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-body text-fg-muted">{t("settings.emailConnection.intro")}</p>
      <div className="flex items-start gap-2 rounded-control bg-warning-soft px-3 py-2 text-caption text-warning">
        <LuTriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
        <p>
          {t("settings.emailConnection.permissionBefore")} <strong>{t("settings.emailConnection.permission")}</strong>{" "}
          {t("settings.emailConnection.permissionAfter")}
        </p>
      </div>
      <Button variant="primary" className="self-start" onClick={connectGoogle} disabled={connecting}>
        {connecting ? t("settings.emailConnection.redirecting") : t("settings.emailConnection.connect")}
      </Button>
    </div>
  );
};
